import React, { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useInView } from 'react-intersection-observer';
import Select, { components } from 'react-select';
import tailwindConfig from 'stylesheets/tailwind.config';

import useDebounce from 'components/hooks/useDebounce';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';

import ChevronRightIcon from '-!svg-react-loader?name=ChevronRightIcon!icons/chevron-right.svg';
import CloseIcon from '-!svg-react-loader?name=CloseIcon!icons/ic-close.svg';
import DocumentIcon from '-!svg-react-loader?name=DocumentIcon!icons/document.svg';
import FolderIcon from '-!svg-react-loader?name=FolderIcon!icons/folder.svg';
import SearchIcon from '-!svg-react-loader?name=SearchIcon!icons/search.svg';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';

const { borderRadius } = tailwindConfig.theme;
const { borderWidth } = tailwindConfig.theme;
const { colors } = tailwindConfig.theme;
const { spacing } = tailwindConfig.theme;

export default function NavigationOptionSearch() {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const [isEnabled, setIsEnabled] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [debouncedSearchParam, _resetDebouncedSearchParam] = useDebounce(
    searchParam,
    250,
  );

  const [loadMoreRef, isLoadMoreInView] = useInView();

  const {
    data: domainNavigationOptions,
    fetchNextPage: fetchDomainNavigationOptionsNextPage,
    hasNextPage: hasDomainNavigationOptionsNextPage,
    isFetching: isFetchingDomainNavigationOptions,
  } = useInfiniteQuery({
    enabled: isEnabled,
    getNextPageParam,
    keepPreviousData: true,
    onError: breadBoard.addInedibleToast,
    queryKey: [
      'projectsDashboardNavigationOptions',
      { search: debouncedSearchParam },
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.post(
        '/projects_dashboard_navigation_option_searches',
        {
          projects_dashboard_navigation_option_search: {
            page: pageParam,
            search: debouncedSearchParam,
          },
        },
      );

      return response.data;
    },
    select: (data) =>
      formatInfiniteData({ data, resourceTypes: ['folder', 'project'] }),
  });

  const options =
    domainNavigationOptions?.data.map((navigationOption) => {
      const sourceId = navigationOption.relationships.source.data.id;
      const sourceType = navigationOption.relationships.source.data.type;
      const source = domainNavigationOptions.included[sourceType][sourceId];
      const Icon = sourceType === 'folder' ? FolderIcon : DocumentIcon;

      return {
        label: source.attributes.name,
        icon: (
          <Icon
            className='tw-flex-none [&_path]:tw-fill-grey-700'
            height={20}
            width={20}
          />
        ),
        href:
          sourceType === 'folder' ?
            `/projects?folder_id=${source.id}`
          : `/projects/${source.id}`,
      };
    }) || [];

  const placeholder = `Search for ${currentActor.division.attributes.singularRAMSName || 'RAMS'}${currentActor.isAllowedFeature('folders') ? ' or folder' : ''}...`;

  const shouldLoadMore =
    !isFetchingDomainNavigationOptions &&
    domainNavigationOptions?.data.length > 0 &&
    hasDomainNavigationOptionsNextPage &&
    isLoadMoreInView;

  if (shouldLoadMore) fetchDomainNavigationOptionsNextPage();

  return (
    <div className='tw-relative tw-w-74'>
      <SearchIcon
        className='tw-absolute tw-left-1 tw-top-1.5 tw-z-[1] [&_path]:tw-fill-grey-200'
        height={24}
        width={24}
      />
      <Select
        blurInputOnSelect={true}
        closeMenuOnSelect={true}
        components={{
          DropdownIndicator: () => null,
          IndicatorsContainer,
          IndicatorSeparator: () => null,
          MenuList,
          Option,
        }}
        filterOption={() => true}
        id='navigationOptionSearch'
        isLoading={isFetchingDomainNavigationOptions}
        loadMoreRef={loadMoreRef}
        name='navigationOptionSearch'
        noOptionsMessage={() =>
          debouncedSearchParam ?
            <>
              No results for "{debouncedSearchParam}". You might want to search
              in Archive or a try a different division.
            </>
          : null
        }
        onInputChange={(input) => {
          setIsEnabled(!!input);
          setSearchParam(input);
        }}
        onInputClear={() => setSearchParam('')}
        openMenuOnFocus={false}
        options={options}
        placeholder={placeholder}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            backgroundColor:
              state.isFocused ? colors.grey[800] : colors.grey[700],
            borderColor: state.isFocused ? colors.blue[300] : colors.grey[900],
            boxShadow: 'none',
            height: spacing[8.5],
            minHeight: spacing[8.5],
            '&:hover': {
              borderColor:
                state.isFocused ? colors.blue[300] : colors.blue[200],
            },
            paddingRight: spacing[2],
          }),
          input: (baseStyles) => ({
            ...baseStyles,
            color: colors.white,
            margin: spacing[0],
            padding: spacing[0],
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            border: `${borderWidth[1]} solid ${colors.blue[100]}`,
            boxShadow: `${spacing[0]} ${spacing[0.5]} ${spacing[1]} ${spacing[0]} rgba(101, 116, 140, 0.12)`,
            zIndex: 1000,
          }),
          menuList: (baseStyles) => ({
            ...baseStyles,
            maxHeight: spacing[71.5],
            borderRadius: borderRadius.lg,
            padding: spacing[0],
          }),
          noOptionsMessage: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.grey['025'],
          }),
          option: () => ({
            '&:hover': {
              backgroundColor: colors.blue['025'],
            },
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: colors.grey[400],
            margin: spacing[0],
          }),
          singleValue: (baseStyles) => ({
            ...baseStyles,
            color: colors.white,
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            cursor: 'text',
            padding: `${spacing[0]} ${spacing[2]} ${spacing[0]} ${spacing[8]}`,
            position: 'initial',
            width: '100%',
          }),
        }}
      />
    </div>
  );
}

function IndicatorsContainer(props) {
  if (!props.selectProps.inputValue) return null;

  return (
    <button
      className='tw-h-5 tw-w-5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-0 hover:tw-bg-grey-700 [&_polygon]:tw-fill-grey-300 [&_polygon]:hover:tw-fill-grey-200'
      onClick={props.selectProps.onInputClear}
    >
      <CloseIcon height={20} width={20} />
      <span className='tw-sr-only'>Clear</span>
    </button>
  );
}

function MenuList(props) {
  return (
    <components.MenuList {...props}>
      {props.children}
      {!!props.selectProps.loadMoreRef && !props.isLoading && (
        <div ref={props.selectProps.loadMoreRef} />
      )}
    </components.MenuList>
  );
}

function Option(props) {
  return (
    <components.Option {...props}>
      <a
        className='tw-group tw-flex tw-items-center tw-justify-between tw-p-3 tw-text-grey-700 hover:tw-text-grey-900'
        href={props.data.href}
      >
        <div className='tw-flex tw-items-center tw-gap-2.5'>
          {props.data.icon}
          <p className='tw-mb-0 tw-line-clamp-3'>{props.label}</p>
        </div>
        <ChevronRightIcon
          className='tw-flex-none tw-opacity-0 group-hover:tw-opacity-100 [&_path]:tw-fill-grey-700'
          height={20}
          width={20}
        />
      </a>
    </components.Option>
  );
}
